import { useDebounceFn } from '@vueuse/core'
import { gql } from '@apollo/client/core'
import { v4 as uuidv4 } from 'uuid'
import OPFSStorage from './opfs'
import BrowserLocalStorage from './browserLocalStorage'
import { type StorageInterface } from '@/common/storageInterface'
import apollo from '@/apollo'
import { Logger } from '@/util'

const log = Logger.get('yb.storage.remote')

const bufferedWritesRoot = 'YM.bufferedWrites'
const lastModifiedPath = 'lastModified'
let nextSequence = 0

type TBufferedWrite = {
  bufferedWriteKey: string
  rootKey: string
  path: string
  content: string
}

export default class RemoteStorage implements StorageInterface {
  private _userName: string
  private _bufferedWriteStorage?: StorageInterface
  private _bufferedWrites: TBufferedWrite[] = []
  private _flushBufferedWrites: (rootKey: string) => void
  private _flushing: boolean = false
  private _displayingError: boolean = false

  constructor(userName: string) {
    this._userName = userName

    // Setup buffer writes flusher.
    this._flushBufferedWrites = useDebounceFn(async () => {
      await this.flushBufferedWritesImpl()
    }, 5000)
  }

  private showError(e: any) {
    if (!this._displayingError) {
      this._displayingError = true
      let m = 'Could not store application settings.'
      if (!!e) {
        m += '  ' + String(e.message || e)
      }
      if (!m.endsWith('.')) {
        m += '.'
      }
      Promise.resolve(useDialogs().error(m))
        .catch(() => {})
        .finally(() => {
          this._displayingError = false
        })
    }
  }

  private async resolvePendingBufferedWrites(): Promise<void> {
    if (!!this._bufferedWriteStorage) {

      try {
        // Read the buffered write keys, which are timestamps
        const bufferedWriteKeys = await this._bufferedWriteStorage.listPath(bufferedWritesRoot, '')
        if (!!bufferedWriteKeys?.length) {

          // Read the latest write to backend storage
          const lastModifiedRemote = await this.readLastModified()

          // We read each buffered write, and write it now if it is newer than the last write to the backend storage.
          for (const bufferedWriteKey of bufferedWriteKeys) {

            // Key is the timestamp.sequence of the buffered write.
            const lastModifiedBufferedWrite = parseInt(bufferedWriteKey.split(/\./)[0])
            if (lastModifiedBufferedWrite > lastModifiedRemote) {

              // Read the payload
              const cachedWriteJSON = await this._bufferedWriteStorage.readPath(bufferedWritesRoot, bufferedWriteKey)
              if (!!cachedWriteJSON) {
                const cachedWrite = JSON.parse(cachedWriteJSON) as TBufferedWrite
                const { rootKey, path, content } = cachedWrite
                await this.writePathImpl(rootKey, path, content)
                await this._bufferedWriteStorage.removePath(bufferedWritesRoot, bufferedWriteKey)
                log.debug(`Replayed buffered write ${rootKey}/${path} (${bufferedWriteKey})`)
              }
            } else {
              log.debug(`Discarding buffered write as timstamp of backend is newer (${lastModifiedRemote} > ${lastModifiedBufferedWrite})`)
            }
          }

          // Clear write buffers.
          await this._bufferedWriteStorage.removePath(bufferedWritesRoot, null)
        }
      } catch (e) {
        log.error(`Could not replay pending buffered writes:`, e)
        this.showError(e)
      }
    }
  }

  private async readLastModified(): Promise<number> {
    const lastModified = await this.readPath(bufferedWritesRoot, lastModifiedPath)
    return lastModified ? parseInt(lastModified) : 0
  }

  private async writeLastModified(lastModified: number): Promise<void> {
    await this.writePathImpl(bufferedWritesRoot, lastModifiedPath, String(lastModified))
  }

  async readPath(rootKey: string, path: string): Promise<string|null> {
    const { data } = await apollo.query<{ storageReadPath: string }>({
      query: gql`
        query storageReadPath($rootKey: String!, $path: String!) {
          storageReadPath(rootKey: $rootKey, path: $path)
        }
      `,
      variables: { rootKey, path }
    })
    return data.storageReadPath
  }

  flushBufferedWritesImpl() {
    if (this._flushing) {
      return
    }

    // This intentionally doesn't do async/await because it is called from a setTimeout and
    // the browser will complain that it takes longer than 250ms.
    // TODO: move to web worker
    try {
      if (!!this._bufferedWriteStorage && this._bufferedWrites.length > 0) {

        // We are flushing, only do this one-at-a-time.
        this._flushing = true

        // Capture buffered writes and clear.
        const bufferedWrites = [...this._bufferedWrites]
        this._bufferedWrites.splice(0, this._bufferedWrites.length)

        // Gather all writes here.
        const writes: Promise<any>[] = []
        for (const bufferedWrite of bufferedWrites) {
          const { bufferedWriteKey, rootKey, path, content } = bufferedWrite
          writes.push(this.writePathImpl(rootKey, path, content))
          writes.push(this._bufferedWriteStorage.removePath(bufferedWritesRoot, bufferedWriteKey))
          log.debug(`Flushed buffered write ${rootKey}/${path} (${bufferedWriteKey})`)
        }

        // Indicate last time we wrote.
        writes.push(this.writeLastModified(Date.now()))

        // Do the writes.
        Promise.all(writes)
          .catch((e) => {
            log.error('Error occurred writing pending buffered writes', e)
            this.showError(e)
          })
          .finally(() => {
            this._flushing = false
          })
      }
    } catch (e) {
      log.error(`Could not flush pending buffered writes:`, e)
      this.showError(e)
    }
  }

  async writePath(rootKey: string, path: string, content: string): Promise<void> {

    // Do we have write buffer storage?
    if (!!this._bufferedWriteStorage) {

      try {
        // If we already have this combination of rootKey/path, remove the current buffered write.
        const index = this._bufferedWrites.findIndex(cw => cw.rootKey === rootKey && cw.path === path)
        if (index >= 0) {
          const { bufferedWriteKey } = this._bufferedWrites[index]
          this._bufferedWrites.splice(index, 1)
          log.debug(`Removing buffered write for (writePath) ${rootKey}/${path} (${bufferedWriteKey})`)
          await this._bufferedWriteStorage.removePath(bufferedWritesRoot, bufferedWriteKey)
          log.debug(`Removed buffered write for (writePath) ${rootKey}/${path} (${bufferedWriteKey})`)
        }

        // Append to journal and write to the write buffer storage.
        const bufferedWriteKey = `${Date.now()}.${++nextSequence}`
        const bufferedWrite: TBufferedWrite = { bufferedWriteKey, rootKey, path, content }
        this._bufferedWrites.push(bufferedWrite)
        await this._bufferedWriteStorage.writePath(bufferedWritesRoot, bufferedWriteKey, JSON.stringify(bufferedWrite))
        log.debug(`Recorded buffered write for (writePath) ${rootKey}/${path} (${bufferedWriteKey})`)

        // Signal flush required.
        this._flushBufferedWrites(rootKey)

      } catch (e) {
        log.error(`Could not record pending writes for (writePath) ${rootKey}/${path}:`, e)
        this.showError(e)
      }
      return
    }

    // Just do the write now then.
    await this.writePathImpl(rootKey, path, content)
  }

  async writePathImpl(rootKey: string, path: string, content: string): Promise<void> {

    //  Each one is separate to avoid overflowing http write buffer limits.
    const batchKey = uuidv4()

    const { data } = await apollo.mutate<{ storageWritePath: boolean }>({
      mutation: gql`
        mutation storageWritePath($rootKey: String!, $path: String!, $content: String!) {
          storageWritePath(rootKey: $rootKey, path: $path, content: $content)
        }
      `,
      variables: { rootKey, path, content, batchKey }
    })
    if (!data?.storageWritePath) { throw new Error('writePath failed') }
  }

  async removePath(rootKey: string, path: string | null | undefined): Promise<void> {

    // Do we have write buffer storage?
    if (!!this._bufferedWriteStorage) {
      try {

        // Do we have a path to remove?
        if (path) {
          // If we already have this combination of rootKey/path, remove the current buffered write.
          while (true) {
            const index = this._bufferedWrites.findIndex(cw => cw.rootKey === rootKey && cw.path.startsWith(path))
            if (index >= 0) {
              const { bufferedWriteKey } = this._bufferedWrites[index]
              this._bufferedWrites.splice(index, 1)
              log.debug(`Removing buffered write for (removePath) ${rootKey}/${path} (${bufferedWriteKey})`)
              await this._bufferedWriteStorage.removePath(bufferedWritesRoot, bufferedWriteKey)
              log.debug(`Removed buffered write for (removePath) ${rootKey}/${path} (${bufferedWriteKey})`)
            } else {
              break
            }
          }

        // No path; just remove buffered writes pending.
        } else {
          this._bufferedWrites.splice(0, this._bufferedWrites.length)
          await this._bufferedWriteStorage.removePath(bufferedWritesRoot, null)
        }
      } catch (e) {
        log.error(`Could not remove pending writes for (removePath) ${rootKey}/${path}:`, e)
      }
    }

    const variables: { rootKey: string; path?: string | null } = { rootKey }
    if (path !== undefined) { variables.path = path } // omit entirely if undefined
    const { data } = await apollo.mutate<{ storageRemovePath: boolean }>({
      mutation: gql`
        mutation storageRemovePath($rootKey: String!, $path: String) {
          storageRemovePath(rootKey: $rootKey, path: $path)
        }
      `,
      variables
    })
    if (!data?.storageRemovePath) { throw new Error('removePath failed') }
  }

  async listPath(rootKey: string, path: string): Promise<string[]> {
    const { data } = await apollo.query<{ storageListPath: string[] }>({
      query: gql`
        query storageListPath($rootKey: String!, $path: String!) {
          storageListPath(rootKey: $rootKey, path: $path)
        }
      `,
      variables: { rootKey, path }
    })
    return data.storageListPath ?? []
  }

  async supported(): Promise<boolean> {

    // Determine if we can do opfs storage for our buffered write storage.
    const opfsStorage = new OPFSStorage(this._userName)
    if (await opfsStorage.supported()) {
      this._bufferedWriteStorage = opfsStorage

    // Fallback to localStorage if we can.
    } else {
      const browserLocalStorage = new BrowserLocalStorage(this._userName)
      if (await browserLocalStorage.supported()) {
        this._bufferedWriteStorage = browserLocalStorage
      }
    }
    await this.resolvePendingBufferedWrites()

    const { data } = await apollo.query<{ storageSupported: boolean }>({
      query: gql`
        query storageSupported {
          storageSupported
        }
      `
    })
    return !!data?.storageSupported
  }
}
